import { DefaultTool } from "@blocksuite/affine-block-surface";
import { translate } from "@blocksuite/affine-shared/services";
import {
  EdgelessDraggableElementController,
  EdgelessToolbarToolMixin,
} from "@blocksuite/affine-widget-edgeless-toolbar";
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";
import { css, html, LitElement } from "lit";
import { state } from "lit/decorators.js";

import {
  addMermaidElement,
  getCollisionAwareMermaidBound,
  trackMermaidCreated,
} from "../create.js";
import { MermaidDragPreview, MermaidIcon } from "../icons.js";
import { MermaidTool } from "../mermaid-tool.js";
import {
  DEFAULT_MERMAID_TEMPLATE,
  type MermaidTemplateId,
} from "../templates.js";
import type { EdgelessMermaidMenu } from "./mermaid-menu.js";

type MermaidDragData = {
  name: "mermaid";
  template: MermaidTemplateId;
};

export class EdgelessMermaidToolButton extends EdgelessToolbarToolMixin(
  LitElement,
) {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }

    .trigger {
      width: 100%;
      height: 64px;
      display: grid;
      place-items: center;
      touch-action: none;
    }

    .trigger > img {
      width: 32px;
      height: 34px;
      object-fit: contain;
      user-select: none;
      pointer-events: none;
    }
  `;

  private _autoUpdateCleanup: (() => void) | null = null;

  private _draggable:
    | EdgelessDraggableElementController<MermaidDragData>
    | undefined;

  @state()
  private accessor _menu: EdgelessMermaidMenu | null = null;

  @state()
  private accessor _selectedTemplate: MermaidTemplateId =
    DEFAULT_MERMAID_TEMPLATE;

  override enableActiveBackground = true;

  override type = MermaidTool;

  private get _blocked() {
    return this.edgeless.std.store.readonly || this.gfx.viewport.locked;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.disposables.add(
      this.gfx.viewport.viewportUpdated.subscribe(() => this.requestUpdate()),
    );
    this.disposables.add(
      this.edgeless.std.store.readonly$.subscribe(() => this.requestUpdate()),
    );
    this.disposables.add(() => {
      this._autoUpdateCleanup?.();
      this._autoUpdateCleanup = null;
    });
  }

  override firstUpdated() {
    this._initDraggable();
  }

  private _closeMenu() {
    this._autoUpdateCleanup?.();
    this._autoUpdateCleanup = null;
    this._menu?.remove();
    this._menu = null;
  }

  private _initDraggable() {
    if (this._draggable || !this.edgeless) return;

    this._draggable = new EdgelessDraggableElementController(this, {
      edgeless: this.edgeless,
      scopeElement: this.toolbarContainer ?? undefined,
      standardWidth: 96,
      clickToDrag: false,
      onElementClick: () => {
        if (!this._blocked) this._toggleMenu();
      },
      onOverlayCreated: () => {
        this._closeMenu();
      },
      onDrop: (element, droppedBound) => {
        if (this._blocked) return;

        const center: [number, number] = [
          droppedBound.x + droppedBound.w / 2,
          droppedBound.y + droppedBound.h / 2,
        ];
        const bound = getCollisionAwareMermaidBound(this.gfx, center);

        this.edgeless.std.store.captureSync();
        const id = addMermaidElement(this.gfx, bound, element.data.template);
        trackMermaidCreated(
          this.gfx,
          element.data.template,
          "toolbar:dnd",
          "drag",
        );

        this.gfx.tool.setTool(DefaultTool);
        this.gfx.selection.set({
          elements: [id],
          editing: false,
        });
      },
    });
  }

  private _openMenu() {
    if (this._menu || this._blocked) return;

    const panel = document.createElement("edgeless-mermaid-menu");
    panel.edgeless = this.edgeless;
    panel.selected = this._selectedTemplate;
    panel.onChange = (template) => {
      this._selectedTemplate = template;
      this._createAtViewport(template);
      this._closeMenu();
    };
    panel.addEventListener("mermaid-menu-close", () => {
      this._closeMenu();
      this.updateComplete
        .then(() =>
          this.renderRoot.querySelector<HTMLElement>(".trigger")?.focus(),
        )
        .catch(console.error);
    });

    this._menu = panel;
    this.renderRoot.append(panel);

    requestAnimationFrame(() => {
      if (!this._menu) return;
      this._autoUpdateCleanup?.();
      this._autoUpdateCleanup = autoUpdate(this, panel, () => {
        computePosition(this, panel, {
          placement: "top",
          middleware: [offset(16), flip({ padding: 8 }), shift({ padding: 8 })],
        })
          .then(({ x, y }) => {
            panel.style.left = `${x}px`;
            panel.style.top = `${y}px`;
          })
          .catch((error) => {
            console.warn("Cannot position Mermaid template menu", error);
          });
      });
    });
  }

  private _toggleMenu() {
    if (this._menu) {
      this._closeMenu();
      return;
    }
    this._openMenu();
  }

  private _createAtViewport(template: MermaidTemplateId) {
    if (this._blocked) return;

    const { x, y } = this.gfx.viewport.center;
    const bound = getCollisionAwareMermaidBound(this.gfx, [x, y]);

    this.edgeless.std.store.captureSync();
    const id = addMermaidElement(this.gfx, bound, template);
    trackMermaidCreated(this.gfx, template, "toolbar:general", "template");

    this.gfx.tool.setTool(DefaultTool);
    this.gfx.selection.set({
      elements: [id],
      editing: false,
    });
  }

  private _dragInfo() {
    return {
      preview: MermaidDragPreview,
      data: {
        name: "mermaid" as const,
        template: this._selectedTemplate,
      },
      standardWidth: 96,
    };
  }

  private _onMouseDown(event: MouseEvent) {
    if (this._blocked) return;
    this._draggable?.onMouseDown(event, this._dragInfo());
  }

  private _onTouchStart(event: TouchEvent) {
    if (this._blocked) return;
    this._draggable?.onTouchStart(event, this._dragInfo());
  }

  private _onKeyDown(event: KeyboardEvent) {
    if (this._blocked) return;
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowUp") {
      event.preventDefault();
      this._toggleMenu();
    } else if (event.key === "Escape" && this._menu) {
      event.preventDefault();
      this._closeMenu();
    }
  }

  override render() {
    const label = translate(this.edgeless.std, "Mermaid") ?? "Mermaid";
    return html`
      <edgeless-toolbar-button
        class="edgeless-mermaid-button"
        .tooltip=${this._menu ? "" : label}
        .tooltipOffset=${5}
        .active=${this.active || !!this._menu}
        .disabled=${this._blocked}
      >
        <div
          class="trigger"
          role="button"
          tabindex=${this._blocked ? "-1" : "0"}
          aria-label=${label}
          aria-haspopup="menu"
          aria-expanded=${String(!!this._menu)}
          aria-disabled=${String(this._blocked)}
          @keydown=${this._onKeyDown}
          @mousedown=${this._onMouseDown}
          @touchstart=${this._onTouchStart}
        >
          ${MermaidIcon}
        </div>
      </edgeless-toolbar-button>
    `;
  }
}
